import { useEffect, useState } from 'react';
import { AlertCircle, Check, ExternalLink, Loader2 } from 'lucide-react';
import { customerDuplicatesApi } from '../../services/customerDuplicatesApi';
import { formatDate, formatDateTimeCH } from '../../utils/format';
import type { PairDetail, PairSide, DecisionStatus } from '../../types/customerDuplicates';
import {
    ALL_REASONS, REASON_LABELS, STATUS_LABELS, parseSecondaryReasons, MARKER_LABELS, ruleLabels,
} from '../../types/customerDuplicates';
import { useToast } from '../ui/Toast';

interface Props {
    pairKey: string;
    canEdit: boolean;
    onDecided: () => void;
}

const STATUS_OPTIONS: { key: DecisionStatus; hint: string }[] = [
    { key: 'OPEN', hint: 'Not decided yet' },
    { key: 'CONFIRMED', hint: 'Confirmed as a duplicate, waiting on cleanup' },
    { key: 'NOT_DUPLICATE', hint: 'Looked at — this is not a duplicate. A reason is required.' },
    { key: 'MERGED', hint: 'The accounts have been merged in the ERP' },
    { key: 'WATCH', hint: 'Deliberately kept separate for now — revisit later' },
];

function eur(n: number): string {
    return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A field row that highlights when the two raw values differ. */
function FieldRow({ label, a, b }: { label: string; a: string | number | null; b: string | number | null }) {
    const sa = a === null || a === undefined || a === '' ? '—' : String(a);
    const sb = b === null || b === undefined || b === '' ? '—' : String(b);
    const differs = sa !== sb;
    return (
        <tr className={differs ? 'bg-amber-50/60' : undefined}>
            <td className="py-1 pr-3 text-xs text-gray-500">{label}</td>
            <td className={`py-1 pr-3 text-sm ${differs ? 'font-medium text-amber-800' : 'text-gray-800'}`}>{sa}</td>
            <td className={`py-1 text-sm ${differs ? 'font-medium text-amber-800' : 'text-gray-800'}`}>{sb}</td>
        </tr>
    );
}

function SidePanel({ label, side }: { label: string; side: PairSide }) {
    const c = side.customer;
    return (
        <div className="rounded border border-gray-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</h4>
                <div className="flex gap-2 text-xs">
                    <a href={side.customer360Url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                        Customer 360 <ExternalLink className="h-3 w-3" />
                    </a>
                    <a href={side.arDunningUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                        AR <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            </div>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
                <dt className="text-gray-500">Account</dt>
                <dd className="text-gray-900">
                    <span className="font-mono text-xs text-gray-500">{c.CustomerID}</span> {c.CustomerName}
                    {Boolean(c.IsBlocked) && <span className="ml-2 text-xs text-red-600">blocked{c.BlockedFor ? ` (${c.BlockedFor})` : ''}</span>}
                </dd>
                <dt className="text-gray-500">Last movement</dt>
                <dd className="text-gray-900">{side.lastMovement ? formatDate(side.lastMovement) : 'never'}</dd>
                <dt className="text-gray-500">Revenue (24m)</dt>
                <dd className="text-gray-900">{eur(side.revenueEur24m)} EUR</dd>
                <dt className="text-gray-500">Open AR</dt>
                <dd className="text-gray-900">{side.openItems} item{side.openItems === 1 ? '' : 's'} · {eur(side.openAmountEur)} EUR</dd>
            </dl>
        </div>
    );
}

export function PairDetailPanel({ pairKey, canEdit, onDecided }: Props) {
    const { toast } = useToast();
    const [detail, setDetail] = useState<PairDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const [status, setStatus] = useState<DecisionStatus>('OPEN');
    const [reasonOverride, setReasonOverride] = useState('');
    const [keepCustomerId, setKeepCustomerId] = useState('');
    const [comment, setComment] = useState('');

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await customerDuplicatesApi.detail(pairKey);
            setDetail(d);
            setStatus(d.decision?.status ?? 'OPEN');
            setReasonOverride(d.decision?.reasonOverride ?? '');
            setKeepCustomerId(d.decision?.keepCustomerId ?? '');
            setComment(d.decision?.comment ?? '');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load the case');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pairKey]);

    const save = async () => {
        if (status === 'NOT_DUPLICATE' && !reasonOverride) {
            toast('A reason is required when marking a pair as not a duplicate', 'error');
            return;
        }
        setBusy(true);
        try {
            await customerDuplicatesApi.decide(pairKey, {
                status,
                reasonOverride: reasonOverride || null,
                keepCustomerId: keepCustomerId || null,
                comment: comment || null,
            });
            toast('Decision saved', 'success');
            await load();
            onDecided();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Could not save the decision', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex h-24 items-center justify-center bg-gray-50">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        </div>;
    }
    if (error || !detail) {
        return <div className="bg-gray-50 px-4 py-3 text-sm text-red-700">{error ?? 'Not found'}</div>;
    }

    const { pair, sideA, sideB, registry, audit } = detail;
    const markers = parseSecondaryReasons(pair.secondaryReasons);

    return (
        <div className="space-y-4 bg-gray-50 px-4 py-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span title={pair.matchedRules}>Matched on: {ruleLabels(pair.matchedRules).join(', ')}</span>
                <span className="text-gray-300">·</span>
                <span>First seen {formatDate(pair.firstSeenAt)}</span>
                <span className="text-gray-300">·</span>
                <span>Last seen {formatDate(pair.lastSeenAt)}</span>
                {!pair.isCurrent && <span className="text-amber-700">— no longer found by the latest run</span>}
            </div>

            {markers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {markers.map(m => (
                        <span key={m} className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                            {MARKER_LABELS[m as keyof typeof MARKER_LABELS] ?? m}
                        </span>
                    ))}
                </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
                <SidePanel label={`Account A — ${pair.companyCodeA.toUpperCase()}`} side={sideA} />
                <SidePanel label={`Account B — ${pair.companyCodeB.toUpperCase()}`} side={sideB} />
            </div>

            <div className="rounded border border-gray-200 bg-white p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Field by field</h4>
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 text-left text-xs text-gray-400">
                            <th className="pb-1 font-medium">Field</th>
                            <th className="pb-1 font-medium">A</th>
                            <th className="pb-1 font-medium">B</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        <FieldRow label="Name" a={sideA.customer.CustomerName} b={sideB.customer.CustomerName} />
                        <FieldRow label="Normalised name" a={sideA.normalized.nameDisplay} b={sideB.normalized.nameDisplay} />
                        <FieldRow label="VAT number" a={sideA.customer.VATNr} b={sideB.customer.VATNr} />
                        <FieldRow label="VAT normalised" a={sideA.normalized.vatNormalized} b={sideB.normalized.vatNormalized} />
                        {(sideA.normalized.vatTruncated || sideB.normalized.vatTruncated) && (
                            <FieldRow label="VAT suffix cut off" a={sideA.normalized.vatSuffix} b={sideB.normalized.vatSuffix} />
                        )}
                        <FieldRow label="Country" a={sideA.customer.CountryISO3} b={sideB.customer.CountryISO3} />
                        <FieldRow label="Street" a={sideA.customer.Street} b={sideB.customer.Street} />
                        <FieldRow label="ZIP / City" a={`${sideA.customer.ZipCode ?? ''} ${sideA.customer.City ?? ''}`} b={`${sideB.customer.ZipCode ?? ''} ${sideB.customer.City ?? ''}`} />
                        <FieldRow label="Business unit" a={sideA.customer.BusinessUnitCode} b={sideB.customer.BusinessUnitCode} />
                        <FieldRow label="Sales manager" a={sideA.customer.SalesManagerID} b={sideB.customer.SalesManagerID} />
                        <FieldRow label="Source system" a={sideA.customer.SourceSystem} b={sideB.customer.SourceSystem} />
                        <FieldRow label="Email" a={sideA.customer.Email} b={sideB.customer.Email} />
                        <FieldRow label="Blocked" a={sideA.customer.IsBlocked ? 'yes' : 'no'} b={sideB.customer.IsBlocked ? 'yes' : 'no'} />
                    </tbody>
                </table>
            </div>

            {(registry.vatValidation.length > 0 || registry.matches.length > 0) && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        External register results
                    </h4>
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-gray-200 text-left text-gray-400">
                                <th className="pb-1 pr-3 font-medium">Account</th>
                                <th className="pb-1 pr-3 font-medium">Source</th>
                                <th className="pb-1 pr-3 font-medium">Status</th>
                                <th className="pb-1 pr-3 font-medium">Registered name</th>
                                <th className="pb-1 pr-3 font-medium">Seat</th>
                                <th className="pb-1 pr-3 font-medium">Official UID</th>
                                <th className="pb-1 font-medium">Checked</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {registry.vatValidation.map((v, i) => (
                                <tr key={`vv-${i}`}>
                                    <td className="py-1 pr-3">{v.customerId === pair.customerIdA ? 'A' : 'B'}</td>
                                    <td className="py-1 pr-3 font-mono">{v.source}</td>
                                    <td className="py-1 pr-3">
                                        {/* ERROR/SKIPPED mean "not checked yet", never "invalid" (P2 §4.2 — a
                                            register outage must never read as a typo-suspicion). */}
                                        {v.status === 'ERROR' || v.status === 'SKIPPED' ? (
                                            <span className="inline-flex items-center gap-1 text-gray-400" title="Throttled or not yet reached — not a verdict">
                                                <AlertCircle className="h-3 w-3" /> not yet checked
                                            </span>
                                        ) : (
                                            v.status
                                        )}
                                    </td>
                                    <td className="py-1 pr-3">{v.registeredName ?? '—'}</td>
                                    <td className="py-1 pr-3">{v.registeredSeat ?? '—'}</td>
                                    <td className="py-1 pr-3 font-mono">{v.registeredUid ?? '—'}</td>
                                    <td className="py-1 text-gray-500">{formatDateTimeCH(v.checkedAt)}</td>
                                </tr>
                            ))}
                            {registry.matches.map((m, i) => (
                                <tr key={`m-${i}`}>
                                    <td className="py-1 pr-3">{m.customerId === pair.customerIdA ? 'A' : 'B'}</td>
                                    <td className="py-1 pr-3 font-mono">{m.source}</td>
                                    <td className="py-1 pr-3">{m.matchQuality}</td>
                                    <td className="py-1 pr-3">{m.registeredName ?? '—'}</td>
                                    <td className="py-1 pr-3">{m.seat ?? '—'}</td>
                                    <td className="py-1 pr-3 font-mono">{m.uid ?? '—'}</td>
                                    <td className="py-1 text-gray-500">{formatDateTimeCH(m.checkedAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {Object.keys(registry.attribution).length > 0 && (
                        <p className="mt-2 text-[10px] text-gray-400">
                            {Object.values(registry.attribution).join(' · ')}
                        </p>
                    )}
                </div>
            )}

            <div className="rounded border border-gray-200 bg-white p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Decision</h4>
                {detail.decision && (
                    <p className="mb-2 text-xs text-gray-500">
                        Last set by {detail.decision.decidedBy} on {formatDateTimeCH(detail.decision.decidedAt)}
                    </p>
                )}
                <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map(o => (
                        <button
                            key={o.key}
                            onClick={() => setStatus(o.key)}
                            disabled={!canEdit || busy}
                            title={o.hint}
                            className={`inline-flex h-8 items-center gap-1.5 rounded border px-3 text-sm disabled:opacity-40 ${
                                status === o.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {status === o.key && <Check className="h-3.5 w-3.5" />}
                            {STATUS_LABELS[o.key]}
                        </button>
                    ))}
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-gray-600">
                            Reason{status === 'NOT_DUPLICATE' ? ' (required)' : ' (override, optional)'}
                        </span>
                        <select
                            value={reasonOverride}
                            onChange={e => setReasonOverride(e.target.value)}
                            disabled={!canEdit || busy}
                            className="h-9 w-full rounded border border-gray-300 px-2 text-sm disabled:bg-gray-50"
                        >
                            <option value="">{status === 'NOT_DUPLICATE' ? 'Select a reason…' : `Keep: ${REASON_LABELS[pair.primaryReason]}`}</option>
                            {ALL_REASONS.map(r => (
                                <option key={r} value={r}>{REASON_LABELS[r]}</option>
                            ))}
                        </select>
                    </label>

                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-gray-600">Which account stays</span>
                        <select
                            value={keepCustomerId}
                            onChange={e => setKeepCustomerId(e.target.value)}
                            disabled={!canEdit || busy}
                            className="h-9 w-full rounded border border-gray-300 px-2 text-sm disabled:bg-gray-50"
                        >
                            <option value="">Not decided</option>
                            <option value={pair.customerIdA}>A — {sideA.customer.CustomerCode} {sideA.customer.CustomerName}</option>
                            <option value={pair.customerIdB}>B — {sideB.customer.CustomerCode} {sideB.customer.CustomerName}</option>
                        </select>
                    </label>
                </div>

                <label className="mt-3 block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Comment</span>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        disabled={!canEdit || busy}
                        rows={2}
                        maxLength={1000}
                        placeholder="What did you find out?"
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                    />
                </label>

                <div className="mt-3 flex items-center gap-2">
                    <button
                        onClick={() => void save()}
                        disabled={!canEdit || busy}
                        className="inline-flex h-8 items-center gap-1.5 rounded bg-blue-600 px-3 text-sm text-white hover:bg-blue-700 disabled:opacity-40"
                    >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save decision
                    </button>
                    {!canEdit && <span className="text-xs text-gray-400">You have read access; deciding needs edit rights.</span>}
                </div>
            </div>

            {audit.length > 0 && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">History</h4>
                    <ul className="space-y-1 text-xs text-gray-600">
                        {audit.map((a, i) => (
                            <li key={i}>
                                {formatDateTimeCH(a.changedAt)} — {a.changedBy} changed <span className="font-medium">{a.field}</span>:{' '}
                                {a.oldValue ?? '—'} → {a.newValue ?? '—'}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
